import {makeAutoObservable, runInAction} from 'mobx';
import ClassApiManager from 'services/API/ClassApi/ClassApiManager';
import {RootStore} from './RootStore';
import strings from '../translations';
import {
  AnalyticsAttentionResponse,
  AnalyticsReactionResponse,
  AnalyticsTranscriptionResponse,
  AttentionType,
  ReactionType,
  StudentType,
  TranscriptionChatData,
  TranscriptionType,
} from 'services/API/ClassApi/ClassApiModels';
import {INITIAL_DATE} from 'constants/calendarConstants';
import {randomColor} from 'helpers/generateRandomColor';
import {toHHMMSS} from 'helpers/formatSecondsToTime';
import simplify from 'simplify-js';
import {Point} from 'recharts/types/shape/Curve';
import {Platform} from 'react-native';
import {LoaderStore} from './LoaderStore';

export interface IAnalyticsStore {
  analyticTypeData: string[];
  analyticType: string;
  duration: number;
  startDate: string;
  allAttendees: StudentType[];
  studentData: StudentType[];
  transcriptionsStudentData: StudentType[];
  reactionStudentData: StudentType[];
  transcriptionChartData: AnalyticsTranscriptionResponse;
  reactionChartData: AnalyticsReactionResponse;
  attentionChartData: AnalyticsAttentionResponse;
  selectedTranscriptionChartData: TranscriptionType[];
  selectedReactionChartData: ReactionType[];
  mappedAttentionChartData: AttentionType[];
  selectedAttentionChartData: number[];
  clearChartsData(): void;
  getTranscriptionAnalytics(id: string): void;
  getReactionAnalatytics(id: string): void;
  getAttentionAnalatytics(id: string): void;
  getAttendees(id: string): void;
}

interface AttentionProps {
  offset: number;
  [key: string]: number;
}

export class AnalyticsStore implements IAnalyticsStore {
  private classApiManager = new ClassApiManager();

  rootStore: RootStore;
  analyticTypeData: string[] = [
    strings.analyticsScreen.speakers,
    strings.analyticsScreen.reaction,
    strings.analyticsScreen.attention,
  ];
  analyticType: string = this.analyticTypeData[0];
  allAttendees: StudentType[] = [
    {
      firstname: '',
      lastname: '',
      email: '',
    },
  ];
  studentData: StudentType[] = [];
  transcriptionsStudentData: StudentType[] = [];
  reactionStudentData: StudentType[] = [];

  transcriptionChartData: AnalyticsTranscriptionResponse = {
    classId: '',
    duration: 90,
    students: [],
  };
  reactionChartData: AnalyticsReactionResponse = {
    classId: '',
    duration: 90,
    students: [],
  };
  attentionChartData: AnalyticsAttentionResponse = {
    classId: '',
    duration: 90,
    students: [],
  };
  transcriptionChatData: TranscriptionChatData[] = [];
  duration: number = 0;
  selectedTranscriptionChartData: TranscriptionType[] = [];
  selectedReactionChartData: ReactionType[] = [];
  mappedAttentionChartData: AttentionType[] = [];
  selectedAttentionChartData: number[] = [];
  startDate: string = INITIAL_DATE;

  attentionAnalyticsColors: string[] = [];

  private loaderStore: LoaderStore;

  constructor(rootStore: RootStore, loaderStore: LoaderStore) {
    this.rootStore = rootStore;
    this.loaderStore = loaderStore;
    makeAutoObservable(this);
  }

  setAnalyticTypeData(value: string[]): void {
    this.analyticTypeData = value;
  }

  setAllAttendees(value: StudentType[]): void {
    this.allAttendees = value;
  }

  setStudentData(value: StudentType[]): void {
    this.studentData = value;
  }

  setTranscriptionsStudentData(value: StudentType[]): void {
    this.transcriptionsStudentData = value;
  }

  setReactionStudentData(value: StudentType[]): void {
    this.reactionStudentData = value;
  }

  setAnalyticType(value: any): void {
    this.analyticType = value;
  }

  setStartDate(value: string): void {
    this.startDate = value;
  }

  setTranscriptionChartData(value: AnalyticsTranscriptionResponse): void {
    this.transcriptionChartData = value;
  }

  setTranscriptionChatData(value: TranscriptionChatData[]): void {
    this.transcriptionChatData = value;
  }

  setReactionChartData(value: AnalyticsReactionResponse): void {
    this.reactionChartData = value;
  }

  setAttentionChartData(value: AnalyticsAttentionResponse): void {
    this.attentionChartData = value;
  }

  setSelectedTranscriptionChartData(value: TranscriptionType[]): void {
    this.selectedTranscriptionChartData = value;
  }

  setSelectedReactionChartData(value: ReactionType[]): void {
    this.selectedReactionChartData = value;
  }

  setSelectedAttentionChartData(value: number[]): void {
    this.selectedAttentionChartData = value;
  }

  clearChartsData() {
    this.setTranscriptionChartData({classId: '', duration: 90, students: []});
    this.setSelectedTranscriptionChartData([]);
    this.setReactionChartData({classId: '', duration: 90, students: []});
    this.setSelectedReactionChartData([]);
    this.setAttentionChartData({classId: '', duration: 90, students: []});
    this.setSelectedAttentionChartData([]);
    this.setMappedAttentionChartData([]);
    this.setTranscriptionsStudentData([]);
    this.setReactionStudentData([]);
  }

  setMappedAttentionChartData(value: AttentionType[]): void {
    this.mappedAttentionChartData = value;
  }

  setDuration(value: number): void {
    this.duration = value;
  }

  selectStudent(value: string) {
    switch (this.analyticType) {
      case strings.analyticsScreen.speakers:
        this.transcriptionChartData.students.map(student => {
          if (student.email === value) {
            const arrayAdaptedValue = student.transcriptions.map(item => {
              return {
                ...item,
                startOffset: item.startOffset,
                endOffset: item.endOffset,
              };
            });
            runInAction(() => {
              this.setSelectedTranscriptionChartData(arrayAdaptedValue);
            });
          }
        });
      case strings.analyticsScreen.reaction:
        this.reactionChartData.students.map(student => {
          if (student.email === value) {
            const arrayAdaptedValue = student.reactions.map(item => {
              return {
                ...item,
                offset: item.offset,
              };
            });
            runInAction(() => {
              this.setSelectedReactionChartData(arrayAdaptedValue);
            });
          }
        });

      case strings.analyticsScreen.attention:
        if (Platform.OS !== 'web') {
          const arrayAdaptedValue: number[] = [];
          this.mappedAttentionChartData.forEach(element => {
            arrayAdaptedValue.push(element[value]);
          });

          runInAction(() => {
            this.setSelectedAttentionChartData(arrayAdaptedValue);
          });
        }
    }
  }

  async getAnalyticsForLesson(id: string) {
    try {
      await this.getAttentionAnalatytics(id);
      await this.getTranscriptionAnalytics(id);
      await this.getReactionAnalatytics(id);
    } catch (error) {
      console.log(error);
    }
  }

  async getTranscriptionAnalytics(id: string) {
    this.clearChartsData();

    try {
      this.loaderStore.startLoading();
      const {classId, duration, students} =
        await this.classApiManager.getTranscriptionAnalytics(id);
      this.loaderStore.stopLoading();

      const studentData: StudentType[] = students.map(item => {
        return {
          firstname: item.firstname,
          lastname: item.lastname,
          email: item.email,
        };
      });

      this.setTranscriptionChartData({
        classId,
        duration,
        students,
      });

      let mappedChatData: TranscriptionChatData[] = [];

      students.map(item => {
        item.transcriptions.map(value => {
          mappedChatData.push({
            firstName: item.firstname,
            lastName: item.lastname,
            startOffset: toHHMMSS(String(value.startOffset)),
            endOffset: toHHMMSS(String(value.endOffset)),
            transcription: value.transcription,
          });
        });
      });

      mappedChatData.sort((a, b) =>
        a.startOffset > b.startOffset
          ? 1
          : b.startOffset > a.startOffset
          ? -1
          : 0,
      );

      this.setTranscriptionChatData(mappedChatData);
      this.setTranscriptionsStudentData(studentData);

      this.setDuration(duration);

      this.selectStudent(this.transcriptionChartData.students[0].email);
    } catch (error) {
      console.log(error);
      this.loaderStore.stopLoading();
    }
  }

  async getReactionAnalatytics(id: string) {
    this.clearChartsData();

    try {
      this.loaderStore.startLoading();
      const {classId, duration, students} =
        await this.classApiManager.getReactionAnalytics(id);
      this.loaderStore.stopLoading();

      const studentData: StudentType[] = students.map(item => {
        return {
          firstname: item.firstname,
          lastname: item.lastname,
          email: item.email,
        };
      });

      this.setReactionChartData({
        classId: classId,
        duration: duration,
        students: students,
      });

      this.setReactionStudentData(studentData);
      this.setDuration(duration);

      this.selectStudent(this.reactionChartData.students[0].email);
    } catch (error) {
      console.log(error);
      this.loaderStore.stopLoading();
    }
  }

  async getAttentionAnalatytics(id: string) {
    try {
      this.loaderStore.startLoading();

      const {classId, duration, students} =
        await this.classApiManager.getAttentionAnalytics(id);
      this.loaderStore.stopLoading();

      let mappedStudentsAttention: AttentionProps[] = Array.from(
        {length: duration},
        (v, i) => {
          return {offset: i};
        },
      );

      let mappedAttention: Point[] = [];
      students.map(item => {
        let key = item.email;
        mappedStudentsAttention.forEach(element => {
          element[key] = 0;
        });
        item.attentions.map(aItem => {
          mappedAttention.push({x: aItem.offset, y: aItem.attention});
        });

        let simplifiedPoints = simplify(mappedAttention, 0.8);

        simplifiedPoints.forEach(element => {
          mappedStudentsAttention[element.x][key] = element.y;
        });
        mappedAttention = [];
      });

      Object.keys(mappedStudentsAttention[0]).map((item, index) => {
        this.attentionAnalyticsColors[index] = `#${randomColor()}`;
      });

      const studentData: StudentType[] = students.map(item => {
        return {
          firstname: item.firstname,
          lastname: item.lastname,
          email: item.email,
        };
      });

      this.setAttentionChartData({
        classId: classId,
        duration: duration,
        students: students,
      });

      this.setMappedAttentionChartData(mappedStudentsAttention);
      this.selectStudent(this.attentionChartData.students[0].email);
      this.setDuration(duration);
      this.setStudentData(studentData);
    } catch (error) {
      console.log(error);
      this.loaderStore.stopLoading();
    }
  }

  async getAttendees(id: string) {
    try {
      this.loaderStore.startLoading();
      const data = await this.classApiManager.getAttendees(id);
      this.loaderStore.stopLoading();

      this.setAllAttendees(data);
    } catch (err) {
      console.log(err);
    }
  }
}
